<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null
  },
  kanbanColumns: {
    type: Array,
    default: () => []
  }
});

const { t } = useI18n();

// Usar as colunas reais do funil do backend
const stageHistory = computed(() => {
  // Usar as colunas reais do funil - pegar do contexto do kanban
  const columns = props.kanbanColumns || [];
  
  if (!columns.length) {
    return [];
  }
  
  // Usar o stage atual do KanbanCard, com fallback para a conversa
  const currentStage = props.kanbanCard?.current_stage || props.conversation?.current_stage || props.conversation?.stage || 'Novos Leads';
  
  // Encontrar o índice da coluna atual
  const currentColumnIndex = columns.findIndex(column => {
    const columnName = column.name?.toLowerCase() || '';
    const currentName = currentStage?.toLowerCase() || '';
    
    // Busca exata primeiro
    if (columnName === currentName) return true;
    
    // Busca por palavras-chave
    if (currentName.includes('novos') && columnName.includes('novos')) return true;
    if (currentName.includes('qualifica') && columnName.includes('qualifica')) return true;
    if (currentName.includes('proposta') && columnName.includes('proposta')) return true;
    if (currentName.includes('negocia') && columnName.includes('negocia')) return true;
    if (currentName.includes('fechado') && columnName.includes('fechado')) return true;
    
    return false;
  });

  // Se não encontrou, assumir que está na primeira coluna
  const stageIndex = currentColumnIndex >= 0 ? currentColumnIndex : 0;

  // Marcar colunas como completed, current ou pending
  return columns.map((column, index) => {
    let status = 'pending';
    
    if (index < stageIndex) {
      status = 'completed';
    } else if (index === stageIndex) {
      status = 'current';
    }

    return {
      id: column.id,
      name: column.name,
      status,
      timestamp: status === 'completed' ? props.kanbanCard?.last_activity_at || props.conversation?.last_activity_at : null,
      user: status === 'completed' ? props.conversation?.assignee : null
    };
  });
});

const progressPercentage = computed(() => {
  const completed = stageHistory.value.filter(s => s.status === 'completed').length;
  return Math.round((completed / stageHistory.value.length) * 100);
});

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script>

<template>
  <div class="w-full">
    <div class="text-sm mb-4">
      <span class="font-medium text-n-slate-11">PROGRESSO DO KANBAN</span>
    </div>
    
    <!-- Stages - Layout horizontal expandido -->
    <div class="flex items-center relative px-4">
      <!-- Background line (cinza) -->
      <div class="absolute top-4 left-8 right-8 h-1 bg-n-slate-3 z-0"></div>
      
      <!-- Progress line (verde) - conecta todas as etapas completadas -->
      <div 
        class="absolute top-4 left-8 h-1 bg-green-500 transition-all duration-500 z-5"
        :style="{ 
          width: stageHistory.length > 1 ? 
            `calc(${(stageHistory.filter(s => s.status === 'completed').length / (stageHistory.length - 1)) * 100}% - 16px)` : 
            '0%'
        }"
      />
      
      <div 
        v-for="(stage, index) in stageHistory" 
        :key="stage.id"
        class="flex flex-col items-center text-center flex-1 relative"
      >
        <!-- Stage Circle -->
        <div
          :class="[
            'w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-all duration-200 relative z-10',
            stage.status === 'completed' ? 'bg-green-500 text-white shadow-md' :
            stage.status === 'current' ? 'bg-n-blue-11 text-white shadow-md ring-2 ring-n-blue-11/30' :
            'bg-n-slate-3 text-n-slate-9'
          ]"
        >
          <i v-if="stage.status === 'completed'" class="i-lucide-check size-4"></i>
          <i v-else-if="stage.status === 'current'" class="i-lucide-circle-dot size-4"></i>
          <span v-else class="text-xs font-semibold">{{ index + 1 }}</span>
        </div>
        
        <!-- Stage Name -->
        <div 
          :class="[
            'text-xs font-medium leading-tight text-center px-1',
            stage.status === 'current' ? 'text-n-blue-11 font-semibold' : 'text-n-slate-11'
          ]"
        >
          {{ stage.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Progress container */
.flex {
  position: relative;
}

/* Stage names responsive text */
@media (max-width: 768px) {
  .text-xs {
    font-size: 0.65rem;
  }
}

/* Hover effects for stages */
.w-8:hover {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

/* Animation for progress bar */
.bg-n-blue-11 {
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
}
</style>